import useResizeObserver from "use-resize-observer";
import { css } from "@emotion/react";
import MuiButton from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";

import { colors } from "@dotnet/telematics-design-system";

import { RectangleLoader } from "shared/loaders/RectangleLoader";
import { svgFontSize } from "shared/styles";

import { useStyleOptions } from "./hooks/useStyleOptions";
import IconLeftButton from "./IconLeftButton";
import IconOnlyButton from "./IconOnlyButton";
import IconRightButton from "./IconRightButton";
import TextOnlyButton from "./TextOnlyButton";
import TwoIconsButton from "./TwoIconsButton";
import {
    BUTTON_COLORS,
    BUTTON_SIZES,
    BUTTON_VARIANTS,
    ButtonProps,
    StyleOptions
} from "./types/";

const Button = ({
    ref,
    children,
    variant,
    size,
    color,
    icon,
    isLoading,
    focus,
    hasRectangleLoader,
    loaderText,
    containerStyles,
    ...props
}: ButtonProps) => {
    const { gray100, gray200, textDisabled, white } = colors;

    const {
        ref: contentRef,
        width: loaderWidth,
        height: loaderHeight
    } = useResizeObserver<HTMLElement>({
        round: notRoundedNumber => notRoundedNumber
    });

    const getDisabledBgColor = () => {
        if (color === BUTTON_COLORS.Transparent) {
            return "rgba(255, 255, 255, 0.05)";
        }

        return color === BUTTON_COLORS.White ? white : gray100;
    };

    const renderButtonVariant = () => {
        switch (variant) {
            case BUTTON_VARIANTS.IconLeft:
                return (
                    <IconLeftButton title={children} icon={icon} size={size} />
                );
            case BUTTON_VARIANTS.IconRight:
                return (
                    <IconRightButton title={children} icon={icon} size={size} />
                );
            case BUTTON_VARIANTS.TwoIcons:
                return <TwoIconsButton title={children} icon={icon} />;
            case BUTTON_VARIANTS.IconOnly:
                return <IconOnlyButton icon={icon} />;
            default:
                return <TextOnlyButton title={children} />;
        }
    };

    const styleOptions: StyleOptions = useStyleOptions();

    const bgColorRest = styleOptions.colors[color].background.rest;
    const bgColorHover = styleOptions.colors[color].background.hover;
    const bgColorActive = styleOptions.colors[color].background.active;
    const bgColorDisabled = getDisabledBgColor();

    const textColor = styleOptions.colors[color].text;
    const textColorDisabled =
        color === BUTTON_COLORS.Transparent
            ? "rgba(255, 255, 255, 0.3)"
            : textDisabled;

    const padding = styleOptions.paddings[variant][size];
    const fontSize = styleOptions.typography[size].fontSize;
    const lineHeight = styleOptions.typography[size].lineHeight;
    const letterSpacing = styleOptions.typography[size].letterSpacing;

    const circularProgressColor = styleOptions.colors[color].loader;
    const circularProgressSize = size === BUTTON_SIZES.Tiny ? 16 : 20;

    return (
        <>
            {hasRectangleLoader && (
                <RectangleLoader height={36} backgroundColor={gray200} />
            )}

            {!hasRectangleLoader && (
                <MuiButton
                    {...props}
                    ref={ref}
                    autoFocus={focus}
                    variant="contained"
                    sx={{
                        textTransform: "none",
                        fontWeight: "600",
                        borderRadius: "6px",
                        boxShadow: "none",
                        minWidth: "unset",
                        backgroundColor: bgColorRest,
                        color: textColor,
                        whiteSpace: "nowrap",
                        fontSize,
                        lineHeight,
                        letterSpacing,
                        padding,
                        pointerEvents: isLoading ? "none" : "auto",

                        "&.Mui-focusVisible": {
                            boxShadow: "none"
                        },

                        "&:hover": {
                            boxShadow: "none",
                            backgroundColor: bgColorHover
                        },

                        "&:active": {
                            boxShadow: "none",
                            backgroundColor: bgColorActive
                        },

                        "&.Mui-disabled": {
                            backgroundColor: bgColorDisabled,
                            color: textColorDisabled,
                            transition: "none"
                        },

                        ...(!isLoading && {
                            "& svg": {
                                ...svgFontSize(
                                    size === BUTTON_SIZES.Tiny ? 16 : 24
                                )
                            }
                        })
                    }}
                >
                    {isLoading ? (
                        <div
                            css={css({
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minWidth: loaderWidth,
                                minHeight: loaderHeight
                            })}
                        >
                            <CircularProgress
                                size={circularProgressSize}
                                sx={{
                                    color: circularProgressColor
                                }}
                            />

                            {loaderText && (
                                <span css={css({ marginLeft: "8px" })}>
                                    {loaderText}
                                </span>
                            )}
                        </div>
                    ) : (
                        <div
                            ref={contentRef}
                            css={css({
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                ...containerStyles
                            })}
                        >
                            {renderButtonVariant()}
                        </div>
                    )}
                </MuiButton>
            )}
        </>
    );
};

export { BUTTON_COLORS, BUTTON_SIZES, BUTTON_VARIANTS } from "./types/";
export default Button;
